import { create } from '@bufbuild/protobuf';
import { createFeatureSelector, createSelector, Store } from '@ngrx/store';
import {
  AuditUpdaterJson,
  AuditUpdaterSchema,
  AuditUpdaterTypeSchema,
  enumEquals,
  enumName,
  enumNumber,
  UserRole,
  UserRoleSchema,
} from '@vmspdb/definitions';
import { first, map } from 'rxjs';
import * as fromAuth from './auth.reducer';

export const selectAuthState = createFeatureSelector<fromAuth.AuthState>(
  fromAuth.authFeatureKey,
);

export const selectAuthUser = createSelector(
  selectAuthState,
  (state) => state.user,
);

export const selectAuthUserId = createSelector(
  selectAuthState,
  (state): string => (state.token?.claims?.['internal_id'] as string) ?? '',
);

export const selectAuthUserIdMultiple = createSelector(
  selectAuthState,
  (state) =>
    state.token?.claims?.['internal_id']
      ? [state.token?.claims?.['internal_id']]
      : [],
);

export const selectAuthToken = createSelector(
  selectAuthState,
  (state) => state.token,
);

export const selectAuthUserRole = createSelector(
  selectAuthState,
  (state): string | null => (state.token?.claims['role'] as string) ?? null,
);

export const selectAuthUserRoleIsManager = createSelector(
  selectAuthState,
  (state): boolean =>
    !!state.token?.claims?.['role'] &&
    [
      enumName(UserRoleSchema, UserRole.MANAGEMENT),
      enumName(UserRoleSchema, UserRole.OWNER),
      enumName(UserRoleSchema, UserRole.SYSOP),
    ].some((role) =>
      enumEquals(UserRoleSchema, role, state.token?.claims?.['role'] as string),
    ),
);

export const selectAuthUserRoleIsSysop = createSelector(
  selectAuthState,
  (state): boolean =>
    !!state.token?.claims?.['role'] &&
    [enumName(UserRoleSchema, UserRole.SYSOP)].some((role) =>
      enumEquals(UserRoleSchema, role, state.token?.claims?.['role'] as string),
    ),
);

export const selectAuthUserRoleIsEmployee = createSelector(
  selectAuthState,
  (state): boolean =>
    !!state.token?.claims?.['role'] &&
    [
      enumName(UserRoleSchema, UserRole.OFFICE_EMPLOYEE),
      enumName(UserRoleSchema, UserRole.FIELD_EMPLOYEE),
    ].some((role) =>
      enumEquals(UserRoleSchema, role, state.token?.claims['role'] as string),
    ),
);

export const selectAuthLoading = createSelector(
  selectAuthState,
  (state) => state.loading,
);
export const selectAuditUpdater = createSelector(
  selectAuthUserId,
  (state): AuditUpdaterJson => ({
    entity: state ? `user:${state}` : 'GUEST',
    type: 'AUDIT_UPDATER_TYPE_FRONTEND',
  }),
);

export function addAuditFrontend<T>(store: Store, item: T) {
  return store.select(selectAuditUpdater).pipe(
    first(),
    // TODO: Look into side-effects
    map((updater) => {
      (item as any).updater = create(AuditUpdaterSchema, {
        ...updater,
        // A message field, so the numeric enum rather than the proto name.
        type: enumNumber(AuditUpdaterTypeSchema, updater.type),
      });
      return item;
    }),
  );
}

export function addAuditBackend<T>(store: Store, item: T) {
  return store.select(selectAuditUpdater).pipe(
    first(),
    // TODO: Look into side-effects
    map((updater) => {
      (item as any).updater = {
        ...updater,
      };
      return item;
    }),
  );
}
